import React, { useEffect, useRef, useState } from "react";
import OutsideClickHandler from "react-outside-click-handler";

import {
  border,
  BorderProps,
  color,
  ColorProps,
  compose,
  EmotionComponent,
  FocusBorderColorProp,
  focusBorderColorProp,
  fontSize,
  HoverColorProp,
  hoverColorProp,
  layout,
  LayoutProps,
  OverrideNativeElementProps,
  SizeProps,
  space,
  SpaceProps,
  styled,
  themeGet,
} from "@catchoftheday/theme";

import { Box } from "../Box";
import { ButtonProps } from "../Button";
import { FlexProps } from "../Flex";
import { Hide, HideProps } from "../Hide";
import { IconDownArrow, IconProps } from "../Icon";
import { ListProps, listStyleProps } from "../List";

type BaseSelectProps = OverrideNativeElementProps<
  HoverColorProp &
    FocusBorderColorProp &
    LayoutProps &
    BorderProps &
    SpaceProps &
    ButtonProps &
    ColorProps &
    SizeProps,
  "button"
>;

export type CustomSelectItemType = {
  component: React.ReactNode;
  label: string;
  disabled?: boolean;
  onSelect?: () => void;
};

export type CustomSelectProps = {
  /** array of React elements to display in the dropdown */
  customSelectItems: CustomSelectItemType[];
  /** props for the container, if the consumer want the component to be full width, it need to set width property rather than display property */
  containerProps?: FlexProps;
  /** prefix icon */
  prefixIcon?: React.FC<IconProps>;
  /** prefix icon colour */
  prefixIconColour?: string;
  /** icon ratio width/height */
  iconRatio?: number;
  /** hide drop down icon */
  dropDownIconHideProps?: HideProps;
  /** the index of the default selected option */
  defaultSelectedIndex?: number;
  /** props for the dropdown menu container */
  menuProps?: ListProps;
  /** Hides current selection when dropdown active */
  hideSelectedOnDropdown?: boolean;
} & BaseSelectProps;

const styleProps = compose(layout, border, space, color, fontSize);

const BaseCustomSelect: EmotionComponent<BaseSelectProps> = styled.button<BaseSelectProps>`
  padding-right: ${themeGet("space.5")};
  text-align: left;

  &:hover:enabled {
    ${hoverColorProp}
    ~ span > svg {
      ${hoverColorProp}
    }
  }

  &:focus {
    ${focusBorderColorProp};
  }

  ${(prop) =>
    prop.maxWidth &&
    `
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;`}

  cursor: pointer;
  ${(prop) => prop.disabled && `cursor: not-allowed;`}

  ${styleProps}
`;

const CustomSelectMenu: EmotionComponent<ListProps> = styled.ul<ListProps>`
  position: absolute;
  margin: 0;
  padding: 0;
  list-style: none;
  top: calc(100% - 1px);
  left: 0;
  right: 0;
  border: solid 1px ${themeGet("colors.ui.borderColor")};
  border-radius: 0 0 4px 4px;
  z-index: 2;
  background-color: white;
  ${listStyleProps}
`;

const CustomSelectItem = styled.li`
  margin: 0;
`;

BaseCustomSelect.defaultProps = {
  py: 5,
  border: 1,
  borderColor: "ui.borderColor",
  focusBorderColor: "secondary",
};

const IconWrapperAfter = styled.span`
  position: absolute;
  top: calc(50% - 0.75em);
  right: 8px;
  padding-right: 10px;
  pointer-events: none;
`;

const IconWrapperBefore = styled.span`
  position: absolute;
  top: calc(50% - 0.75em);
  left: 0.75em;
`;

export const CustomSelect: React.FC<CustomSelectProps> = ({
  containerProps,
  prefixIcon: PrefixIcon,
  prefixIconColour,
  iconRatio,
  disabled = false,
  dropDownIconHideProps,
  children,
  customSelectItems,
  defaultSelectedIndex,
  menuProps,
  hideSelectedOnDropdown,
  ...otherProps
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(
    defaultSelectedIndex
  );

  useEffect(() => {
    setSelectedIndex(defaultSelectedIndex);
  }, [defaultSelectedIndex]);

  const optionsRef = useRef<(HTMLLIElement | null)[]>([]);

  function triggerAction(item: CustomSelectItemType, index: number) {
    if (item.disabled) return;
    if (item.onSelect) {
      setSelectedIndex(index);
      item.onSelect();
    }
    setDropdownOpen(false);
  }

  function handleKeyEvents(
    event: React.KeyboardEvent,
    item: CustomSelectItemType,
    index: number
  ) {
    if (event.key === "Enter") {
      triggerAction(item, index);
    }
    if (event.key === "ArrowDown" && optionsRef.current[index + 1]) {
      optionsRef.current[index + 1]?.focus();
    }
    if (event.key === "ArrowUp" && optionsRef.current[index - 1]) {
      optionsRef.current[index - 1]?.focus();
    }
  }

  return (
    <Box position="relative" alignItems="center" {...containerProps}>
      <OutsideClickHandler onOutsideClick={() => setDropdownOpen(false)}>
        <BaseCustomSelect
          aria-disabled={disabled}
          disabled={disabled}
          type="button"
          aria-haspopup="listbox"
          width={containerProps?.width ? "100%" : "auto"}
          pl={PrefixIcon ? "2.5rem" : "1.25rem"}
          borderRadius={dropdownOpen ? "4px 4px 0 0" : "4px"}
          pr="45px"
          color={disabled ? "typography.inactive" : "typography.text"}
          bg="white"
          tabIndex={0}
          onFocus={() => setDropdownOpen(true)}
          onMouseDown={(e) => {
            setDropdownOpen(!dropdownOpen);
            e.stopPropagation();
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown" && optionsRef.current[0]) {
              optionsRef.current[0]?.focus();
            }
          }}
          {...otherProps}
        >
          {dropdownOpen == true && hideSelectedOnDropdown == true
            ? ""
            : selectedIndex !== undefined
            ? customSelectItems[selectedIndex].label
            : children}
        </BaseCustomSelect>
        <Hide {...dropDownIconHideProps}>
          <IconWrapperAfter>
            <IconDownArrow
              width="0.8em"
              height="0.4em"
              color={disabled ? "typography.inactive" : "inherit"}
              title="select drop down arrow"
            />
          </IconWrapperAfter>
        </Hide>
        {PrefixIcon && (
          <IconWrapperBefore>
            {
              <PrefixIcon
                height="1.5em"
                width={`${1.5 * (iconRatio || 0.5)}em`}
                color={
                  disabled
                    ? "typography.inactive"
                    : prefixIconColour || "inherit"
                }
              />
            }
          </IconWrapperBefore>
        )}
        {dropdownOpen && (
          <CustomSelectMenu role="listbox" {...menuProps}>
            {customSelectItems.map((item, index) => (
              <CustomSelectItem
                ref={(elem) => (optionsRef.current[index] = elem)}
                id={item.label}
                role="option"
                aria-selected={index === selectedIndex}
                key={index}
                tabIndex={0}
                onClick={() => triggerAction(item, index)}
                onKeyDown={(e) => handleKeyEvents(e, item, index)}
              >
                {item.component}
              </CustomSelectItem>
            ))}
          </CustomSelectMenu>
        )}
      </OutsideClickHandler>
    </Box>
  );
};

CustomSelect.displayName = "CustomSelect";
CustomSelect.defaultProps = {
  containerProps: {
    color: "typography.text",
  },
  dropDownIconHideProps: {},
};

export default CustomSelect;
